import { BookOpenIcon, ExternalLinkIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type {
  ReadUserGuidePageArgs,
  ReadUserGuidePageResult,
} from '@/features/agent/tools/user-guide'
import { useToolCardOpen } from '@/features/agent/tools/useToolCardOpen'
import { ToolCallCard, ToolCallHeader, type ToolCallStatus } from './_shared/ToolCallHeader'

export interface ReadUserGuidePageViewProps {
  args: ReadUserGuidePageArgs
  parsed: ReadUserGuidePageResult | null
  running: boolean
  errored: boolean
  /** Force the card expanded even with no body. Stories pass true. */
  defaultOpen?: boolean
}

// Phone: vertical padding turns each text link into a ~28 px tap target.
const LINK_CLASS =
  'inline-flex items-center gap-1 text-xs text-primary underline-offset-2 hover:underline max-md:py-1.5'

/**
 * Pure presentation for a `read_user_guide_page` tool-result card: the page
 * the assistant read, with a link into the user guide. The card is the
 * evidence for a product answer, so it names the page and its sections rather
 * than repeating the page text the assistant already summarised.
 */
export function ReadUserGuidePageView({
  args,
  parsed,
  running,
  errored,
  defaultOpen = false,
}: ReadUserGuidePageViewProps) {
  const { t } = useTranslation()
  const ok = parsed?.status === 'ok'
  const failed = errored || (parsed != null && !ok)

  const label = running
    ? t('chat.userGuide.reading')
    : failed
      ? t('chat.userGuide.readFailed')
      : (parsed?.title ?? args.slug)

  const headerStatus: ToolCallStatus = running ? 'running' : failed ? 'error' : 'complete'
  const description = (
    <>
      {label}
      {(running || failed) && (
        <code className="ml-1 rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-foreground">
          {args.slug}
        </code>
      )}
    </>
  )

  const hasBody = !running && parsed != null
  const { open, onToggle } = useToolCardOpen(hasBody, defaultOpen, failed)

  return (
    <ToolCallCard>
      <ToolCallHeader
        status={headerStatus}
        icon={BookOpenIcon}
        toolName="read_user_guide_page"
        description={description}
        expandable={hasBody}
        open={open}
        onToggle={onToggle}
      />
      {open && parsed && (
        <div className="flex flex-col gap-2 border-t border-border px-4 py-3 text-xs">
          {ok ? <PageBody parsed={parsed} /> : <FailureBody parsed={parsed} />}
        </div>
      )}
    </ToolCallCard>
  )
}

/** The page the assistant read: summary, fallback note, section links, open link. */
function PageBody({ parsed }: { parsed: ReadUserGuidePageResult }) {
  const { t } = useTranslation()
  const url = parsed.url
  return (
    <>
      {parsed.summary && <p className="text-foreground">{parsed.summary}</p>}
      {parsed.not_translated && (
        <p className="text-muted-foreground">{t('chat.userGuide.notTranslated')}</p>
      )}
      {url && parsed.sections && parsed.sections.length > 0 && (
        <div className="flex flex-col gap-0.5">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {t('chat.userGuide.sections')}
          </span>
          <ul className="flex flex-wrap gap-x-3 gap-y-0.5 max-md:gap-x-4 max-md:gap-y-0">
            {parsed.sections.map(s => (
              <li key={s.anchor}>
                <a
                  href={`${url}#${s.anchor}`}
                  target="_blank"
                  rel="noreferrer"
                  className={LINK_CLASS}
                >
                  {s.heading}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
      {url && (
        <a href={url} target="_blank" rel="noreferrer" className={LINK_CLASS}>
          {t('chat.userGuide.openPage')}
          <ExternalLinkIcon className="size-3" />
        </a>
      )}
    </>
  )
}

/** Why there is no page: guide missing in this deployment, unknown slug, or a raw error. */
function FailureBody({ parsed }: { parsed: ReadUserGuidePageResult }) {
  const { t } = useTranslation()
  if (parsed.status === 'unavailable') {
    return <p className="text-muted-foreground">{t('chat.userGuide.unavailable')}</p>
  }
  if (parsed.status === 'unknown_slug') {
    return (
      <p className="text-muted-foreground">
        {t('chat.userGuide.unknownSlug')}
        {parsed.valid_slugs && parsed.valid_slugs.length > 0 && (
          <span className="ml-1 font-mono text-[11px]">{parsed.valid_slugs.join(', ')}</span>
        )}
      </p>
    )
  }
  return <p className="whitespace-pre-wrap text-muted-foreground">{parsed.error}</p>
}
